// The UI displaying an invoice line by line, with a n-way matching status per line.
//   Each line provides an expandable panel with details about its n-way matching.
import { clone, colors, DxTheme } from '@dx-ui/dx-common';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import WarningIcon from '@material-ui/icons/ErrorOutline';
import { get } from 'lodash';
import React, { FC } from 'react';
import { Datagrid, ListContextProvider, useTranslate } from 'react-admin';
import { renderColumns } from '../CommonColumns';
import InvoiceSummaryField from '../InvoiceSummaryField';
import {
  ExchangeRate,
  FieldProps,
  I2oMatching,
  I2oSuggestion,
  INVOICE_ICONS,
  MatchingOrderLine,
  OrderLineSnapshot,
  Price,
  SuggestedOrderLine,
  Task,
  UblInvoice,
  UblOrder,
} from '../types';

const useStyles: any = makeStyles(
  (theme: DxTheme) => ({
    root: {
      '& > div': {
        boxShadow: 'unset',
      },
    },
    headerCell: {
      padding: '5px 10px',
      verticalAlign: 'middle',
      '&.alignRight': {
        textAlign: 'right',
      },
      '&.alignRight span[role=button]': {
        justifyContent: 'flex-end', // sortable columns
      },
      '&:last-child': {
        padding: '5px 10px',
      },
      '& span[role=button]': {
        verticalAlign: 'unset', // https://docprocess.atlassian.net/browse/DXPOR-349
        display: 'flex',
      },
    },
    row: {
      verticalAlign: 'text-bottom',
    },
    headerRow: {
      '&:hover .stickyCol': {
        backgroundColor: theme.app.tableBkgColor,
      },
    },
    header: {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(1),
    },
    tableScroll: {
      // // wrapper div css class
      // position: 'relative',
      // width: 'calc(100vw - 370px)',
      // zIndex: 1,
      // overflow: 'auto',
      // '& table': {
      //   borderCollapse: 'separate',
      // },
    },
    warningIcon: {
      marginRight: 3,
    },
  }),
  { name: 'I2oNwayMatchedInvoiceField' }
);

const i18nKey =
  'dxMessages.task.invoice_matching.invoiceHoldDecisionTask.cockpit.';

// The invoice, line by line with n-way matching details.
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
const I2oNwayMatchedInvoiceField: FC<FieldProps<Task<InvoiceFlowVariables>>> = (
  props
) => {
  const { record, locale, resource } = props;
  const translate = useTranslate();
  const classes = useStyles();

  if (record == null) return null;

  const {
    virtualInvoice,
    correctionInvoiceChain,
    rollback,
    completeCluster,
    error,
    matching = {},
    suggestion = {},
    orderLineSnapshots = [],
    exchangeRates = [],
  } = record.flow.variables;

  const invoiceLines = virtualInvoice?.InvoiceLine || [];

  const rowByIds: { [key: string]: ExpandPanelInfo } = invoiceLines.reduce(
    (acc, line) => {
      const row: ExpandPanelInfo = clone(line); // Do not modify the orginal UBL unvoice.
      const lineId = get(row, 'ID.value');
      acc[lineId] = row;

      // Set the total value (without VAT)
      // set(column, Ubl.totalWithoutVat, computeTotalWithoutVat(column));

      const lineMatching: Array<MatchingOrderLine> = get(matching, lineId, []);
      const lineSuggestion: Array<SuggestedOrderLine> = get(
        suggestion,
        lineId,
        []
      );

      // Compute the quantity of item perfectly matched (right price & line value).
      const matchedQuantity = lineMatching.reduce(
        (acc, recadvLine) => acc + recadvLine.quantity,
        0
      );

      // Compute the quantity of item imperfectly matched (bad unitary price or line value).
      const suggestedQuantity = lineSuggestion.reduce(
        (acc, recadvLine) => acc + recadvLine.quantity,
        0
      );

      // Compute the quantity of item imperfectly matched but with the right unitary price.
      const suggestedQuantityWithRightUnitaryPrice = lineSuggestion
        .filter((recadvLine) => recadvLine.hints.includes('UNITARY_PRICE_OK'))
        .reduce((acc, recadvLine) => acc + recadvLine.quantity, 0);

      // Compute the list of the prices which may be wrong.
      const recadvUnitaryPrices = lineSuggestion.map(
        (line) => line.unitaryPriceWoGreenTaxAndVat
      );

      // Compute the already invoiced quantity.
      const alreadyInvoicedQuantity = orderLineSnapshots
        .filter(
          (s) =>
            lineMatching.some(
              (line) => line.orderId === s.id && line.orderLineId === s.lineId
            ) ||
            lineSuggestion.some(
              (line) => line.orderId === s.id && line.orderLineId === s.lineId
            )
        )
        .reduce((acc, s) => acc + s.quantity, 0);

      const expectedQuantity = get(row, 'InvoicedQuantity.value');
      row.extension = {
        matched: matchedQuantity === expectedQuantity,
        matchedByQuantity:
          matchedQuantity + suggestedQuantity === expectedQuantity,
        matchedByQuantityAndUnitaryPrice:
          matchedQuantity + suggestedQuantityWithRightUnitaryPrice ===
          expectedQuantity,
        matchedQuantity: matchedQuantity,
        suggestedQuantity: suggestedQuantity,
        suggestedQuantityWithRightUnitaryPrice:
          suggestedQuantityWithRightUnitaryPrice,
        alreadyInvoicedQuantity: alreadyInvoicedQuantity,
        recadvUnitaryPrices: recadvUnitaryPrices,
      };

      return acc;
    },
    {}
  );

  return (
    <div>
      {rollback && (
        <Typography
          variant='caption'
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <WarningIcon style={{ color: colors.brightOrange, fontSize: 20 }} />
          &nbsp;{translate(i18nKey + 'i2oRaceConditionOnInvoiceApproval')}
        </Typography>
      )}
      {error && (
        <Typography
          variant='caption'
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <WarningIcon
            style={{
              color: colors.functionalRed,
              fontSize: 20,
            }}
            className={classes.warningIcon}
          />
          <ErrorField error={error} translate={translate} />
        </Typography>
      )}
      {!error && !completeCluster && (
        <Typography
          variant='caption'
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <WarningIcon style={{ color: colors.brightOrange, fontSize: 20 }} />
          &nbsp;{translate(i18nKey + 'i2oIncompleteCluster')}
        </Typography>
      )}
      {(error || rollback || !completeCluster) && (
        <div style={{ margin: '1.5em' }} />
      )}
      <Grid container direction='column' spacing={8}>
        <Grid item>
          <InvoiceSummaryField
            invoice={virtualInvoice}
            correctionInvoiceChain={correctionInvoiceChain}
            icon={INVOICE_ICONS(virtualInvoice?.InvoiceTypeCode.value, {
              fontSize: 30,
              color: colors.lightPurple,
            })}
          />
        </Grid>
        {exchangeRates && exchangeRates.length > 0 && (
          <div
            style={{ marginTop: '1em', marginRight: '2em', textAlign: 'end' }}
          >
            <Typography variant='caption'>
              {translate(i18nKey + 'usedExchangeRate')}
            </Typography>
            <Grid container direction='column'>
              {exchangeRates.map((rate, i) => (
                <Grid item key={i}>
                  <ExchangeRateField exchangeRate={rate} />
                </Grid>
              ))}
            </Grid>
          </div>
        )}
        <Grid item>
          <ListContextProvider
            value={{
              ids: Object.keys(rowByIds),
              data: rowByIds,
              currentSort: { field: 'id', order: 'DESC' },
              resource,
              selectedIds: [],
            }}
          >
            <Datagrid
              classes={{
                row: classes.row,
                rowCell: classes.linesCell,
                headerCell: classes.headerCell,
                tbody: classes.tbody,
              }}
              //@ts-ignore
              expand={<ExpandPanel {...props} />}
            >
              {renderColumns(classes, locale)}
            </Datagrid>
          </ListContextProvider>
        </Grid>
      </Grid>
    </div>
  );
};

// Display the error message.
// error is a stringified NwayMatchingError.
const ErrorField: FC<{ error: string; translate: any }> = ({
  error,
  translate,
}) => {
  try {
    const json = JSON.parse(error);
    const params = json.params || {};
    return (
      <Grid container direction='column'>
        <Grid item>
          <Typography variant='caption'>
            {translate(`${i18nKey}errors.${json.key}`, {
              _: json.message,
              ...params,
            })}
          </Typography>
        </Grid>
        {(json.details || []).map((detail, i) => {
          const params = detail.params || {};
          return (
            <Grid item key={i}>
              <Typography variant='caption' noWrap>
                &bull;&nbsp;
                {translate(`${i18nKey}errors.${detail.key}`, {
                  _: detail.message,
                  ...params,
                })}
              </Typography>
            </Grid>
          );
        })}
      </Grid>
    );
  } catch (e) {
    // Should never happen.
    return <Typography variant='caption'>{error}</Typography>;
  }
};

const ExpandPanel: FC<FieldProps<ExpandPanelInfo>> = (props) => {
  const { record } = props;
  const translate = useTranslate();

  if (record == null) return null;

  const info = record.extension;
  const gross = record.Price.PriceAmount.value;
  const greenTax =
    record?.AllowanceCharge?.find((ac) => ac?.ChargeIndicator.value)
      ?.PerUnitAmount.value || 0;
  const discount =
    record?.AllowanceCharge?.find((ac) => !ac?.ChargeIndicator.value)
      ?.MultiplierFactorNumeric.value || 0;

  const unitaryPrice = (
    gross -
    (gross * discount) / 100 +
    greenTax
  ).toLocaleString(undefined, {
    style: 'currency',
    currency: get(record, 'Price.PriceAmount.currencyID'),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const recadvUnitaryPrices = info.matched
    ? unitaryPrice
    : info.recadvUnitaryPrices
        .map((p) =>
          p.value.toLocaleString(undefined, {
            style: 'currency',
            currency: p.currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        )
        .join(',') || '--';

  return (
    <Table padding='none'>
      <TableHead>
        <TableRow>
          <TableCell>{translate(i18nKey + 'element')}</TableCell>
          <TableCell>{translate(i18nKey + 'rules')}</TableCell>
          <TableCell>
            {translate('dxMessages.invoices.document_label')}
            <br />
            <span style={{ fontSize: 'xx-small' }}>
              ({translate(i18nKey + 'unitaryNetPriceAlgo')})
            </span>
          </TableCell>
          <TableCell>
            {translate('dxMessages.orders.document_label')}
            <br />
            <span style={{ fontSize: 'xx-small' }}>
              ({translate(i18nKey + 'acceptedMinusInvoiced')})
            </span>
          </TableCell>
          <TableCell> {translate(i18nKey + 'match')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow key='quantity'>
          <TableCell component='th' scope='row'>
            <span style={{ color: colors.lightPurple, fontWeight: 'bolder' }}>
              {translate('dxMessages.headers.quantity')}
            </span>
          </TableCell>
          <TableCell>{translate(i18nKey + 'i2oQuantityRule')}</TableCell>
          <TableCell>{get(record, 'InvoicedQuantity.value')}</TableCell>
          <TableCell>{info.matchedQuantity + info.suggestedQuantity}</TableCell>
          <TableCell>
            {info.matchedByQuantity ? (
              <CheckIcon
                style={{
                  color: colors.functionalGreen,
                  verticalAlign: 'bottom',
                }}
              />
            ) : (
              <ClearIcon
                style={{
                  color: colors.brightOrange,
                  verticalAlign: 'bottom',
                }}
              />
            )}
          </TableCell>
        </TableRow>
        <TableRow key='price'>
          <TableCell component='th' scope='row'>
            <span style={{ color: colors.lightPurple, fontWeight: 'bolder' }}>
              {translate(i18nKey + 'unitaryPrice')}
            </span>
          </TableCell>
          <TableCell>{translate(i18nKey + 'i2oUnitaryPriceRule')}</TableCell>
          <TableCell>{unitaryPrice}</TableCell>
          <TableCell>{recadvUnitaryPrices}</TableCell>
          {info.matchedQuantity + info.suggestedQuantity === 0 && (
            <TableCell>N/A</TableCell>
          )}
          {info.matchedQuantity + info.suggestedQuantity !== 0 && (
            <TableCell>
              {info.matched ||
              (info.matchedQuantity + info.suggestedQuantity > 0 &&
                info.suggestedQuantity ===
                  info.suggestedQuantityWithRightUnitaryPrice) ? (
                <CheckIcon
                  style={{
                    color: colors.functionalGreen,
                    verticalAlign: 'bottom',
                  }}
                />
              ) : (
                <ClearIcon
                  style={{
                    color: colors.brightOrange,
                    verticalAlign: 'bottom',
                  }}
                />
              )}
            </TableCell>
          )}
        </TableRow>
      </TableBody>
    </Table>
  );
};

const ExchangeRateField: FC<ExchangeRateFieldProps> = ({ exchangeRate }) => {
  const locale = undefined;

  const toFromRate = Intl.NumberFormat(locale, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(1 / exchangeRate.value);

  const fromToRate = Intl.NumberFormat(locale, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(exchangeRate.value);

  return (
    <div
      style={{
        padding: '5px',
        fontSize: 'smaller',
      }}
    >
      1 {exchangeRate.to} → {toFromRate} {exchangeRate.from}
      <br />
      <span style={{ color: colors.functionalGray }}>
        1 {exchangeRate.from} → {fromToRate} {exchangeRate.to}
      </span>
      <br />
      <span style={{ color: colors.functionalGray }}>
        source: {exchangeRate.source} - {exchangeRate.at}
      </span>
    </div>
  );
};

interface ExchangeRateFieldProps {
  exchangeRate: ExchangeRate;
}

interface InvoiceFlowVariables {
  companyId: string;
  // The invoice under matching. Always present.
  virtualInvoice: UblInvoice;
  // The correction chain, when virtualInvoice is a corrective invoice.
  correctionInvoiceChain?: Array<UblInvoice>;
  // The orders belonging to the cluster.
  orders?: Array<UblOrder>;
  // A flag indicating whether all the orders have been retrieved.
  completeCluster?: boolean;
  // A flag indicating whether the invoice was totally matched (all the lines match)
  invoiceMatched?: boolean;
  // The lines that was perfectly matched.
  matching?: I2oMatching;
  // The lines that was imperfectly matched (bad unitary price,... but the item identifications
  // matches)
  suggestion?: I2oSuggestion;
  // The quantities already invoiced on the order lines.
  orderLineSnapshots?: Array<OrderLineSnapshot>;
  // The currency exchange rates used during the macthing (when orders and invoice use different
  // currencies)
  exchangeRates?: Array<ExchangeRate>;
  // A flag indicating that this invoice was accepted previously but another invoice on the same
  // order lines was also accepted just before and removed some quantities on the recadv lines
  // that was matched by this invoice: so its acceptation was rollback-ed and its n-way matching
  // re-played.
  rollback: boolean;
  // The stringified JSON error ({key: "<error_id>", message: "<msg in English>", params: ...}).
  error?: string;
}

// The data provided to the line of the expandable panel (detail of the line n-way matching).
interface ExpandPanelInfo {
  id: string;
  [key: string]: any; // The UBL invoice.
  extension: {
    matched: boolean; // Perfect match: quantity, unitary price, line value.
    matchedByQuantityAndUnitaryPrice: boolean; // Only matched by quantity and unitary price.
    matchedByQuantity: boolean; // Not by unitary price or line value.
    matchedQuantity: number;
    suggestedQuantity: number;
    suggestedQuantityWithRightUnitaryPrice: number;
    alreadyInvoicedQuantity: number;
    recadvUnitaryPrices: Array<Price>;
  };
}

export default I2oNwayMatchedInvoiceField;
